// ─── BonsACocher.jsx ─────────────────────────────────────────────────
// Ce qu'Anissa doit cocher sur le bon papier, une fois les analyses validées.
//
// Elle a les bons d'Ortho devant elle, devant la cliente. Le bon principal est
// EN ALLEMAND et elle ne lit pas l'allemand : elle fait correspondre des
// chaînes de caractères à l'œil, sur quatre pages.
//
// D'où les partis pris d'affichage :
//   - le libellé exact imprimé est le plus gros élément de la ligne
//   - la traduction vit dessous, en second
//   - le numéro de section ne guide que l'œil, il ne désigne jamais seul
//   - rien sous 12 px, personne ne lit du 10 px sur un écran de cabinet

import { repartirSurLesBons } from '../services/clinical/catalog/orthoBonsDeCommande';

const VERT = '#1a2e1f';
const ENCRE = '#2e2c28';
const DOUX = '#5c6459';
const IVOIRE = '#fbfaf5';
const BORD = '#ddd8c8';
const AMBRE_FOND = '#fdf6e7';
const AMBRE_BORD = '#d9a441';
const AMBRE_ENCRE = '#5a3d08';

export default function BonsACocher({ codes = [], tarifBonChf = 21.6 }) {
  const { bons, inconnus } = repartirSurLesBons(codes);
  if (bons.length === 0 && inconnus.length === 0) return null;

  const totalFrais = Math.ceil(bons.length * tarifBonChf);

  return (
    <section aria-label="Cases à cocher sur les bons de commande">
      <p style={{ fontSize: 13.5, lineHeight: 1.55, color: DOUX, margin: '0 0 16px' }}>
        Coche ces cases sur les bons Ortho, puis donne-les à la cliente.
        {' '}Compare le libellé, pas seulement le numéro de section.
      </p>

      {bons.map(({ bon, lignes }) => (
        <article
          key={bon.id}
          style={{
            border: `1px solid ${BORD}`, borderRadius: 12,
            background: IVOIRE, padding: '14px 16px', marginBottom: 12,
          }}
        >
          <header style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', marginBottom: 12,
          }}>
            <strong style={{ fontSize: 14.5, color: VERT }}>{bon.label}</strong>
            <span style={{ fontSize: 12.5, color: DOUX }}>
              {lignes.length} case{lignes.length > 1 ? 's' : ''} · {bon.version}
            </span>
          </header>

          {lignes.map((ligne) => (
            <div
              key={ligne.code}
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                padding: '9px 0', borderTop: `1px solid ${BORD}`,
              }}
            >
              {/* Une case dessinée, pas un vrai input : rien n'est à saisir
                  ici, le geste se fait sur le papier. */}
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto', width: 15, height: 15, marginTop: 2,
                  border: `1.5px solid ${VERT}`, borderRadius: 3,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: ENCRE, lineHeight: 1.35 }}>
                  {ligne.libelle}
                </div>
                {ligne.aide && (
                  <div style={{ fontSize: 12.5, color: DOUX, lineHeight: 1.45, marginTop: 2 }}>
                    {ligne.aide}
                  </div>
                )}
                <div style={{ fontSize: 12, color: DOUX, marginTop: 4 }}>
                  section {ligne.section} · {ligne.prelevement}
                  {ligne.kit ? ` · kit ${ligne.kit}` : ''}
                </div>
              </div>
            </div>
          ))}

          {/* Le consentement n'est pas à la même page selon le bon. Une
              signature oubliée fait refuser le prélèvement. */}
          <p style={{
            fontSize: 12.5, color: AMBRE_ENCRE, background: AMBRE_FOND,
            border: `1px solid ${AMBRE_BORD}`, borderRadius: 8,
            margin: '12px 0 0', padding: '8px 11px', lineHeight: 1.45,
          }}>
            <strong>{bon.consentement}</strong>, sans quoi le laboratoire refuse le prélèvement.
          </p>
        </article>
      ))}

      {inconnus.length > 0 && (
        <p role="alert" style={{
          fontSize: 13, color: AMBRE_ENCRE, background: AMBRE_FOND,
          border: `1px solid ${AMBRE_BORD}`, borderLeft: `3px solid #b3781a`,
          borderRadius: 8, padding: '10px 13px', lineHeight: 1.5, margin: '0 0 12px',
        }}>
          <strong>
            {inconnus.length === 1
              ? 'Une analyse n’est pas dans le guide des bons.'
              : `${inconnus.length} analyses ne sont pas dans le guide des bons.`}
          </strong>
          {' '}Cherche-la toi-même sur le bon, et signale-le pour qu’on l’ajoute :
          {' '}{inconnus.join(', ')}.
        </p>
      )}

      {bons.length > 0 && (
        <p style={{ fontSize: 13, color: DOUX, margin: 0, lineHeight: 1.5 }}>
          {bons.length === 1
            ? 'Un seul bon à envoyer'
            : `${bons.length} bons à envoyer`}
          {' · '}
          <strong style={{ color: ENCRE }}>{totalFrais}.— de frais de dossier</strong>
          {bons.length === 1 && ', le minimum possible'}
        </p>
      )}
    </section>
  );
}
